/**
 * S2 — Trial-Expired Post-Login Modal
 *
 * Fires once per user-session on dashboard mount when a flag expired in the last 72h.
 * Centered · max-w 560 · shadow · radius 16.
 *
 * Lists all expired features with red "Expired" badges.
 * Reassurance note: "Your patient data is safe and unaffected."
 *
 * Admin:     "Remind me later" + "Upgrade now" (blue)
 * Non-admin: "Remind me later" + "Notify admin" (blue)
 *
 * Figma: S2 — Trial-Expired Post-Login Modal
 */
import React, { useState, useEffect } from "react";
import type { FeatureFlagState } from "../../hooks/useFeatureFlags";

interface TrialExpiredModalProps {
  expiredFlags: FeatureFlagState[];
  isAdmin: boolean;
  onUpgradeClick: () => void;
  onNotifyAdmin: () => void;
  subscriptionExpired?: boolean;
}

const SESSION_KEY = "trial_expired_modal_shown";

export const TrialExpiredModal: React.FC<TrialExpiredModalProps> = ({
  expiredFlags,
  isAdmin,
  onUpgradeClick,
  onNotifyAdmin,
  subscriptionExpired = false,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (expiredFlags.length === 0) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    setVisible(true);
  }, [expiredFlags]);

  if (!visible) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setVisible(false);
  };

  const handleAction = () => {
    handleDismiss();
    if (isAdmin) onUpgradeClick();
    else onNotifyAdmin();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[560px] w-full p-4 sm:p-6 md:p-8 relative">
        {/* Close */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
          aria-label="Close"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-1.5 sm:mb-2 pr-6">
          {subscriptionExpired ? "Your subscription has expired" : "Your trial has ended"}
        </h2>
        <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-5">
          {subscriptionExpired
            ? "Your plan has expired. Renew to continue using premium features:"
            : "These premium features are now paused and require an active subscription:"}
        </p>

        {/* Expired feature list */}
        <div className="space-y-2 mb-4 sm:mb-5">
          {expiredFlags.map(f => (
            <div
              key={f.feature}
              className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 sm:px-4 sm:py-3"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm sm:text-base">{f.icon}</span>
                <span className="text-xs sm:text-sm font-medium text-slate-800">{f.display_label}</span>
              </div>
              <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-1.5 sm:px-2 py-0.5">
                <HourglassIcon />
                Expired
              </span>
            </div>
          ))}
        </div>

        {/* Reassurance */}
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-2.5 sm:p-3 mb-5 sm:mb-6 flex items-start gap-2">
          <span className="text-amber-600 flex-shrink-0 mt-0.5 text-sm">🛡️</span>
          <p className="text-[11px] sm:text-xs text-amber-800 leading-relaxed">
            Your patient data is safe and unaffected. All existing records, visits, prescriptions,
            and invoices remain accessible. Only the features above require an active subscription.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
          <button
            onClick={handleDismiss}
            className="w-full sm:w-auto px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors order-2 sm:order-1"
          >
            Remind me later
          </button>
          <button
            onClick={handleAction}
            className="w-full sm:w-auto px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors order-1 sm:order-2"
          >
            {isAdmin ? "Upgrade now" : "Notify admin"}
          </button>
        </div>
      </div>
    </div>
  );
};

const HourglassIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M17 2v4.172a2 2 0 0 1-.586 1.414L12 12l-4.414-4.414A2 2 0 0 1 7 6.172V2" />
  </svg>
);

export default TrialExpiredModal;
